import { Unstable_NumberInput as BaseNumberInput } from '@mui/base/Unstable_NumberInput'
import AddIcon from '@mui/icons-material/Add'
import RemoveIcon from '@mui/icons-material/Remove'
import WarningAmberIcon from '@mui/icons-material/WarningAmber'
import { TableCell, TableRow } from '@mui/material'
import Tooltip from '@mui/material/Tooltip'
import { styled } from '@mui/system'
import React from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { cambiarCantidadArticulo } from '../../actions/excelPedido'
import { translateMessage } from '../../lang/idiomas'

const ExcelPedidoTabla = ({ lin }) => {
  const dispatch = useDispatch()

  const updatedLinea = useSelector(state => {
    // Comprueba si state.lineas existe y es un array
    if (Array.isArray(state.excelPedido.lineasExcel)) {
      return state.excelPedido.lineasExcel.find(l => l.rowNum === lin.rowNum)
    }
    // Si state.lineas no es un array, devuelve la línea original
    return null
  })

  const handleChange = (e, newValue) => {
    let lineaCambiada = updatedLinea
    const value = parseInt(e.target.value || newValue)
    let valorAjustado = value
    if (value === '' || /^[0-9]+$/.test(value)) {
      if (lineaCambiada['validez'] === 'O' && (value !== 0) & (value !== '')) {
        lineaCambiada['validez'] = 'S'
      }
      if (value === 0 || value === '') {
        lineaCambiada['validez'] = 'O'
      }

      if (
        lineaCambiada['validez'] === 'W' &&
        value &&
        value > Number(lineaCambiada.cantidad_minima)
      ) {
        lineaCambiada['validez'] = 'S'
      }

      // Controlamos la cantidad mínima del artículo y ajustamos
      // el posible valor introducido al mínimo si no llega
      if (lineaCambiada.cantidad_minima && value < Number(lineaCambiada.cantidad_minima)) {
        valorAjustado = Number(lineaCambiada.cantidad_minima)
        lineaCambiada['validez'] = 'W'
      }

      // Controlamos el múltiplo del artículo y ajustamos el posible
      // valor introducido manualmente al siguiente múltiplo
      if (lineaCambiada.multiplo) {
        const resto = value % lineaCambiada.multiplo
        if (resto !== 0) {
          valorAjustado = value + (lineaCambiada.multiplo - resto)
        }
      }

      // Controlamos al final el precio,
      // si no se obtiene precio la línea no es válida
      if (lineaCambiada.precio === 0) {
        lineaCambiada['validez'] = 'N'
      }

      lineaCambiada['cantidad'] = valorAjustado
      dispatch(cambiarCantidadArticulo(lineaCambiada))
    }
  }

  const handleButtonClick = step => () => {
    const newValue = (updatedLinea.cantidad || 0) + step
    handleChange({ target: { value: newValue } })
  }

  const getBgColorRow = linea => {
    switch (linea.validez) {
      case 'W': // línea con advertencia o cantidad 0
      case 'O':
        return '#f3edc7'
      case 'N': // línea inválida
        return '#f3c8cec2'
      default: // línea correcta
        return '#fff'
    }
  }

  if (!updatedLinea) return null

  return (
    <TableRow
      key={updatedLinea.articulo}
      sx={{
        backgroundColor: getBgColorRow(updatedLinea),
      }}>
      <TableCell
        component='th'
        scope='row'>
        {updatedLinea.articulo}
      </TableCell>
      <TableCell
        component='th'
        scope='row'>
        {updatedLinea.descripcion}
      </TableCell>
      <TableCell
        component='th'
        scope='row'>
        <BaseNumberInput
          min={Number(updatedLinea.cantidad_minima) || 0}
          step={updatedLinea.multiplo || 1}
          disabled={updatedLinea.validez === 'N'}
          slots={{
            root: StyledInputRoot,
            input: StyledInput,
            incrementButton: StyledButton,
            decrementButton: StyledButton,
          }}
          slotProps={{
            incrementButton: {
              children: <AddIcon fontSize='small' />,
              className: 'increment',
              onClick: handleButtonClick(1),
            },
            decrementButton: {
              children: <RemoveIcon fontSize='small' />,
              onClick: handleButtonClick(-1),
            },
          }}
          align='right'
          value={updatedLinea.cantidad || 0}
          onChange={(event, value) => handleChange(event, value)}
          onBlur={event => handleChange(event)}
        />
      </TableCell>
      <TableCell align='right'>{updatedLinea.precio || '0'}</TableCell>
      <TableCell align='center'>
        {updatedLinea.reservado_a02 &&
          (updatedLinea.validez === 'W' || updatedLinea.validez === 'N') && (
            <Tooltip title={translateMessage(updatedLinea.reservado_a02)}>
              <WarningAmberIcon />
            </Tooltip>
          )}
      </TableCell>
    </TableRow>
  )
}

const blue = {
  100: '#daecff',
  200: '#b6daff',
  300: '#66b2ff',
  400: '#3399ff',
  500: '#007fff',
  600: '#0072e5',
  700: '#0059B2',
  800: '#004c99',
}

const grey = {
  50: '#F3F6F9',
  100: '#E5EAF2',
  200: '#DAE2ED',
  300: '#C7D0DD',
  400: '#B0B8C4',
  500: '#9DA8B7',
  600: '#6B7A90',
  700: '#434D5B',
  800: '#303740',
  900: '#1C2025',
}

const StyledInputRoot = styled('div')(
  ({ theme }) => `
    font-family: 'IBM Plex Sans', sans-serif;
    font-weight: 400;
    color: ${theme.palette.mode === 'dark' ? grey[300] : grey[500]};
    display: flex;
    flex-flow: row nowrap;
    justify-content: center;
    align-items: center;
  `,
)

const StyledInput = styled('input')(
  ({ theme }) => `
    font-size: 0.875rem;
    font-family: inherit;
    font-weight: 400;
    line-height: 1.375;
    color: ${theme.palette.mode === 'dark' ? grey[300] : grey[900]};
    background: ${theme.palette.mode === 'dark' ? grey[900] : '#fff'};
    border: 1px solid ${theme.palette.mode === 'dark' ? grey[700] : grey[200]};
    box-shadow: 0px 2px 4px ${
      theme.palette.mode === 'dark' ? 'rgba(0,0,0, 0.5)' : 'rgba(0,0,0, 0.05)'
    };
    border-radius: 8px;
    margin: 0 8px;
    padding: 10px 12px;
    outline: 0;
    min-width: 0;
    width: 4rem;
    text-align: center;
  
    &:hover {
      border-color: ${blue[400]};
    }
  
    &:focus {
      border-color: ${blue[400]};
      box-shadow: 0 0 0 3px ${theme.palette.mode === 'dark' ? blue[700] : blue[200]};
    }
  
    &:focus-visible {
      outline: 0;
    }
  `,
)

const StyledButton = styled('button')(
  ({ theme }) => `
    font-family: 'IBM Plex Sans', sans-serif;
    font-size: 0.875rem;
    box-sizing: border-box;
    line-height: 1.5;
    border: 1px solid;
    border-radius: 999px;
    border-color: ${theme.palette.mode === 'dark' ? grey[800] : grey[200]};
    background: ${theme.palette.mode === 'dark' ? grey[900] : grey[50]};
    color: ${theme.palette.mode === 'dark' ? grey[200] : grey[900]};
    width: 32px;
    height: 32px;
    display: flex;
    flex-flow: row nowrap;
    justify-content: center;
    align-items: center;
    transition-property: all;
    transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
    transition-duration: 120ms;
  
    &:hover {
      cursor: pointer;
      background: ${theme.palette.mode === 'dark' ? blue[700] : blue[500]};
      border-color: ${theme.palette.mode === 'dark' ? blue[500] : blue[400]};
      color: ${grey[50]};
    }
  
    &:focus-visible {
      outline: 0;
    }
  
    &.increment {
      order: 1;
    }
  `,
)

export default ExcelPedidoTabla
